import React, { useCallback, useEffect, useMemo, useState } from "react";
import PatrolDrawingZoomViewport from "./PatrolDrawingZoomViewport.jsx";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import VideocamIcon from "@mui/icons-material/Videocam";
import { Viewer, Worker, SpecialZoomLevel } from "@react-pdf-viewer/core";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.js";
import * as pdfjsLib from "pdfjs-dist";
import {
  getDrawingFileType,
  getDrawingMediaUrl,
} from "./patrolDrawingMedia.js";

import "@react-pdf-viewer/core/lib/styles/index.css";

if (typeof pdfjsLib.GlobalWorkerOptions !== "undefined") {
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
}

const DEMO_WALLS = [
  [10, 12, 88, 12],
  [10, 12, 10, 88],
  [10, 88, 88, 88],
  [88, 12, 88, 88],
  [10, 48, 72, 48],
  [48, 12, 48, 48],
  [72, 48, 72, 88],
];

async function pdfBlobUrlToThumbnailDataUrl(blobUrl) {
  const res = await fetch(blobUrl);
  const buffer = await res.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 1.5 });
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext("2d");
  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas.toDataURL("image/png");
}

function DemoFloorPlanSvg({ onReady }) {
  useEffect(() => {
    onReady?.();
  }, [onReady]);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid meet"
      style={{
        display: "block",
        pointerEvents: "none",
        maxWidth: "100%",
        maxHeight: "100%",
      }}
    >
      {DEMO_WALLS.map(([x1, y1, x2, y2], i) => (
        <line
          key={i}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="rgba(255,255,255,0.55)"
          strokeWidth={1.2}
        />
      ))}
    </svg>
  );
}

function markerCssPosition(m) {
  if (m.xPercent != null && m.yPercent != null) {
    return {
      left: `${m.xPercent * 100}%`,
      top: `${m.yPercent * 100}%`,
    };
  }
  if (m.x != null && m.y != null) {
    return { left: `${m.x}%`, top: `${m.y}%` };
  }
  return { left: "50%", top: "50%" };
}

function MarkerPins({ markers, zoom = 1 }) {
  const pinScale = zoom > 0 ? 1 / zoom : 1;

  return markers.map((m, i) => (
    <Box
      key={
        m.cameraId != null && m.locationIndex != null
          ? `${m.cameraId}-${m.locationIndex}`
          : m.cameraId || `${m.x}-${m.y}-${i}`
      }
      sx={{
        position: "absolute",
        ...markerCssPosition(m),
        transform: `translate(-50%, -50%) scale(${pinScale})`,
        transformOrigin: "center center",
        pointerEvents: "none",
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          bgcolor: "#22C55E",
          border: "2px solid rgba(255,255,255,0.9)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        }}
      >
        <VideocamIcon sx={{ fontSize: 12, color: "#fff" }} />
      </Box>
      {m.label ? (
        <Typography
          sx={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            mt: 0.25,
            fontSize: 10,
            fontWeight: 600,
            color: "#fff",
            textShadow: "0 1px 2px rgba(0,0,0,0.8)",
            whiteSpace: "nowrap",
            maxWidth: 100,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {m.label}
        </Typography>
      ) : null}
    </Box>
  ));
}

function PdfMediaLayer({ fileUrl, compact }) {
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        bgcolor: "#fff",
        overflow: "hidden",
        pointerEvents: "none",
        "& .rpv-core__viewer": { height: "100%" },
        "& .rpv-core__inner-pages": { overflow: "hidden !important" },
      }}
    >
      <Worker workerUrl={workerSrc}>
        <Viewer
          fileUrl={fileUrl}
          defaultScale={
            compact ? SpecialZoomLevel.PageFit : SpecialZoomLevel.PageFit
          }
        />
      </Worker>
    </Box>
  );
}

function ImageMediaLayer({ src, objectFit = "contain", onMediaReady }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return <DemoFloorPlanSvg onReady={onMediaReady} />;
  }

  return (
    <Box
      component="img"
      src={src}
      alt=""
      draggable={false}
      onLoad={() => onMediaReady?.()}
      onError={() => setFailed(true)}
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit,
        pointerEvents: "none",
        userSelect: "none",
        bgcolor: "#fff",
      }}
    />
  );
}

function PdfThumbnailLayer({ fileUrl, onMediaReady }) {
  const [thumbUrl, setThumbUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setThumbUrl(null);

    pdfBlobUrlToThumbnailDataUrl(fileUrl)
      .then((url) => {
        if (!cancelled) {
          setThumbUrl(url);
          if (url) onMediaReady?.();
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  if (loading) {
    return (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#F3F4F6",
        }}
      >
        <CircularProgress size={28} sx={{ color: "#2932E5" }} />
      </Box>
    );
  }

  if (failed || !thumbUrl) {
    return (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#E5E7EB",
        }}
      >
        <Typography sx={{ fontSize: 12, color: "#6B7280" }}>PDF</Typography>
      </Box>
    );
  }

  return (
    <ImageMediaLayer
      src={thumbUrl}
      objectFit="contain"
      onMediaReady={onMediaReady}
    />
  );
}

/**
 * Renders patrol drawing as image, PDF, or demo floor plan with optional markers.
 *
 * @param {{
 *   drawing?: { fileUrl?: string, imagePreview?: string, fileType?: string, fileName?: string } | null,
 *   markers?: Array,
 *   minHeight?: number,
 *   compact?: boolean,
 *   interactive?: boolean,
 *   enableZoom?: boolean,
 *   placementHint?: boolean,
 *   onCanvasClick?: (e: React.MouseEvent) => void,
 *   onLocationPick?: (coords: { xPercent: number, yPercent: number }) => void,
 * }} props
 */
export default function PatrolDrawingMediaSurface({
  drawing = null,
  markers = [],
  minHeight = 168,
  compact = false,
  interactive = false,
  enableZoom = false,
  placementHint = false,
  onCanvasClick,
  onLocationPick,
}) {
  const fileType = getDrawingFileType(drawing);
  const mediaUrl = getDrawingMediaUrl(drawing);
  const [mediaVersion, setMediaVersion] = useState(0);

  const usePdfThumbnail = compact || enableZoom;

  const handleMediaReady = useCallback(() => {
    setMediaVersion((v) => v + 1);
  }, []);

  const renderMedia = () => {
    const ready = enableZoom ? handleMediaReady : undefined;
    if (fileType === "pdf" && mediaUrl) {
      if (usePdfThumbnail) {
        return (
          <PdfThumbnailLayer fileUrl={mediaUrl} onMediaReady={ready} />
        );
      }
      return <PdfMediaLayer fileUrl={mediaUrl} compact={compact} />;
    }
    if (fileType === "image" && mediaUrl) {
      const fit = compact && !enableZoom ? "cover" : "contain";
      return (
        <ImageMediaLayer src={mediaUrl} objectFit={fit} onMediaReady={ready} />
      );
    }
    if (drawing?.imagePreview && fileType !== "pdf") {
      return (
        <ImageMediaLayer
          src={drawing.imagePreview}
          objectFit={compact ? "cover" : "contain"}
          onMediaReady={ready}
        />
      );
    }
    return <DemoFloorPlanSvg onReady={ready} />;
  };

  const media = useMemo(
    () => renderMedia(),
    [drawing, fileType, mediaUrl, compact, enableZoom, usePdfThumbnail],
  );

  if (enableZoom) {
    const pickHandler =
      interactive && (onLocationPick || onCanvasClick)
        ? (coords) => {
            if (onLocationPick) {
              onLocationPick(coords);
            }
          }
        : undefined;

    return (
      <PatrolDrawingZoomViewport
        minHeight={minHeight}
        placementHint={placementHint}
        borderActive={placementHint}
        mediaVersion={mediaVersion}
        onLocationPick={pickHandler}
        markers={<MarkerPins markers={markers} />}
      >
        {media}
      </PatrolDrawingZoomViewport>
    );
  }

  return (
    <Box
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onCanvasClick : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") onCanvasClick?.(e);
            }
          : undefined
      }
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight,
        bgcolor: "#7C8494",
        overflow: "hidden",
        cursor: interactive && placementHint ? "crosshair" : "default",
        border: interactive && placementHint ? "2px solid #2932E5" : "none",
        outline: "none",
      }}
    >
      {media}
      <MarkerPins markers={markers} />
    </Box>
  );
}
